import {
  callOnBeforeRun,
  type ChatEvent,
  ChatUiEventTypes,
  sendChatEvent,
  sendChatUiEvent,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import {
  FullChat,
  type ContentVariants as ChatUiContentVariants,
  type RunContextCallback,
} from '@trimble-agentic-internal-npm-local/agentic-chat-ui';
import { useCallback, useContext } from 'react';

import { useHostTools } from '../hooks/useHostTools';
import { useMcpAppOpenLink } from '../hooks/useMcpAppOpenLink';
import { EmbeddedConfigContext } from '../providers/embedded-config-provider';
import {
  resolveEmbeddedVariant,
  trackEmbeddedAgentSelected,
  trackEmbeddedChatEvent,
  trackEmbeddedNewChat,
} from '../utilities/embeddedAnalytics';
import { subscribeToParentSendMessage } from '../utilities/parentSendMessage';
import { mapDynamicToolsToRunContext } from '../utilities/toolUtils';

export const EmbeddedFullChat = () => {
  const context = useContext(EmbeddedConfigContext);
  const hostTools = useHostTools();
  const onMcpAppOpenLink = useMcpAppOpenLink();
  const variant = resolveEmbeddedVariant(context?.chatConfig.uiConfig.variant);

  const handleChatInputButtonClick = useCallback((buttonId: string) => {
    sendChatUiEvent({
      type: ChatUiEventTypes.OnChatInputButtonClick,
      payload: buttonId,
    });
  }, []);

  const handleChatEvent = useCallback(
    (event: ChatEvent) => {
      sendChatEvent(event.type, event.payload);
      trackEmbeddedChatEvent(variant, event, context?.chatConfig.agentId);
    },
    [context?.chatConfig.agentId, variant]
  );

  const handleOnBeforeRun = useCallback(
    async (
      agentId: string
    ): Promise<Awaited<ReturnType<RunContextCallback>>> => {
      const dynamicRunContext = await callOnBeforeRun(
        agentId,
        context?.chatConfig.onBeforeRunTimeout
      );

      return mapDynamicToolsToRunContext(dynamicRunContext);
    },
    [context?.chatConfig.onBeforeRunTimeout]
  );

  return (
    <div className='h-[100dvh] w-[100dvw]'>
      <FullChat
        agentId={context?.chatConfig.agentId || ''}
        threadId={context?.chatConfig.threadId}
        chatInputButtons={context?.chatConfig.uiConfig.chatInput.buttons}
        onChatInputButtonClick={handleChatInputButtonClick}
        hideModelSelection={
          context?.chatConfig.uiConfig.chatInput.hideModelSelection
        }
        contentVariant={
          context?.chatConfig.uiConfig.contentVariant as
            ChatUiContentVariants | undefined
        }
        onBeforeRunCall={handleOnBeforeRun}
        onChatEvent={handleChatEvent}
        hostTools={hostTools}
        onMcpAppOpenLink={onMcpAppOpenLink}
        mcpAppConfig={context?.chatConfig.mcpAppConfig}
        promptTiles={context?.chatConfig.uiConfig.promptTiles}
        showThinking={context?.chatConfig.uiConfig.showThinking}
        showLandingAgentDropdown={
          context?.chatConfig.uiConfig.showLandingAgentDropdown
        }
        localToolCancelledMessage={
          context?.chatConfig.localToolCancelledMessage
        }
        externalSendMessageSource={subscribeToParentSendMessage}
        onAgentSelect={agentId => {
          sendChatUiEvent({
            type: ChatUiEventTypes.OnAgentSelect,
            payload: agentId,
          });
          trackEmbeddedAgentSelected(variant, agentId);
        }}
        onThreadSelect={() => {
          sendChatUiEvent({
            type: ChatUiEventTypes.OnThreadSelect,
            payload: undefined,
          });
        }}
        onNewChat={() => {
          sendChatUiEvent({
            type: ChatUiEventTypes.OnNewChat,
            payload: undefined,
          });
          trackEmbeddedNewChat(variant, context?.chatConfig.agentId);
        }}
        onExploreAgents={() => {
          sendChatUiEvent({
            type: ChatUiEventTypes.OnExploreAgents,
            payload: undefined,
          });
        }}
        onCreateAgent={() => {
          sendChatUiEvent({
            type: ChatUiEventTypes.OnCreateAgent,
            payload: undefined,
          });
        }}
      />
    </div>
  );
};
